import * as XLSX from 'xlsx';
import debug from 'debug';
import { saveBlob } from './saveBlob';
import { toXlsxBlob } from './xlsxBlob';
import { columnsWithLineBreaks } from './xlsxWrapText';

const log = debug('rois:utils:table-to-excel');

if (import.meta.env.DEV) {
  debug.enable('rois:utils:table-to-excel*');
}

interface ExcelConfig {
  filename: string;
  ignoreColumns: number[];
  filterByIDs: string[];
  filterPositionColumn: number;
  compositeKeyColumns?: number[];
}

const defaultConfig: ExcelConfig = {
  filename: 'table.xlsx',
  ignoreColumns: [],
  filterByIDs: [],
  filterPositionColumn: 1,
};

/**
 * Normalizes text extracted from DOM for reliable comparison.
 * Strips non-breaking spaces, zero-width characters, and normalizes whitespace.
 */
function normalizeText(text: string | null | undefined): string {
  if (text === null || text === undefined) return '';
  return text
    .replace(/\u00A0/g, ' ')       // non-breaking space -> regular space
    .replace(/\u200B/g, '')         // zero-width space
    .replace(/\u200C/g, '')         // zero-width non-joiner
    .replace(/\u200D/g, '')         // zero-width joiner
    .replace(/\uFEFF/g, '')         // BOM / zero-width no-break space
    .replace(/\s+/g, ' ')           // collapse multiple whitespace
    .trim();
}


/**
 * Celdas que cuentan para la exportación: descarta las marcadas con
 * `data-export-ignore` (p. ej. la columna de la bandera) ANTES de aplicar
 * cualquier índice, para que `ignoreColumns` y `filterPositionColumn` de cada
 * pantalla sigan apuntando donde siempre. Exportada para que quien calcule
 * esos índices (el respaldo de `DownloadHistoricModal`) cuente igual.
 */
export function exportCells(row: HTMLTableRowElement | null | undefined): HTMLTableCellElement[] {
  return Array.from(row?.cells ?? []).filter(
    (c) => !c.hasAttribute('data-export-ignore')
  );
}

export class TableToExcel {
  private options: ExcelConfig;
  private table: HTMLTableElement | null;
  private tableTitles: string[] = [];
  private dataRows: string[][] = [];

  constructor(tableId: string, options: object = {}) {
    this.options = {
      ...defaultConfig,
      ...options,
    };
    this.table = this.getTableElement(tableId);
    this.init();
  }

  getTableElement(tableId: string): HTMLTableElement | null {
    return document.querySelector(tableId);
  }

  init(): void {
    if (this.table && this.table.tHead) {
      this.tableTitles = this.findTableTitles(this.table.tHead);
      this.dataRows = this.generateRowData(this.table.tBodies);
    }
  }

  findTableTitles(heads: HTMLTableSectionElement): string[] {
    const thead: HTMLTableRowElement | null = heads.rows.item(0);
    const cells = exportCells(thead);
    const titles: string[] = [];
    for (let i = 0; i < cells.length; i++) {
      if (this.options.ignoreColumns.includes(i)) continue;
      titles.push(cells[i]?.textContent?.trim() ?? '');
    }

    return titles;
  }

  generateRowData(
    tbody: HTMLCollectionOf<HTMLTableSectionElement>
  ): string[][] {
    const rows: HTMLCollectionOf<HTMLTableRowElement> | undefined =
      tbody?.item(0)?.rows;
    const result: string[][] = [];

    log('generateRowData - Debug Info:');
    log('Total rows found:', rows?.length || 0);
    log('Filter options:', this.options);

    // Pre-compute: check if we're using composite keys and normalize filter values once
    const isCompositeKey = this.options.filterByIDs.some((id) =>
      id.includes('|')
    );
    const normalizedFilterIDs = this.options.filterByIDs.map(normalizeText);
    const compositeKeyCols = this.options.compositeKeyColumns || [2, 3];

    log('isCompositeKey:', isCompositeKey);
    log('compositeKeyColumns:', compositeKeyCols);
    log('normalizedFilterIDs:', normalizedFilterIDs);

    // If no filter IDs provided, include all rows
    const includeAll = normalizedFilterIDs.length === 0;

    if (rows) {
      for (let i = 0; i < rows.length; i++) {
        const tr = exportCells(rows?.item(i));

        let isIncluded = includeAll;

        if (!includeAll && isCompositeKey) {
          // For composite keys, build the key from configured columns
          const keyParts = compositeKeyCols.map((colIdx) =>
            normalizeText(tr[colIdx]?.textContent)
          );
          const compositeKey = keyParts.join('|');

          isIncluded = normalizedFilterIDs.some(
            (filterKey) => filterKey === compositeKey
          );
          log(
            `Row ${i}: Composite key "${compositeKey}" (parts: ${JSON.stringify(keyParts)}), Included: ${isIncluded}`
          );
        } else if (!includeAll) {
          // Regular filtering by single column
          const cellContent = normalizeText(
            tr[this.options.filterPositionColumn]?.textContent
          );
          isIncluded = normalizedFilterIDs.some(
            (filterVal) => filterVal === cellContent
          );
          log(
            `Row ${i}: Cell[${this.options.filterPositionColumn}] = "${cellContent}", Included: ${isIncluded}`
          );
        }

        if (!isIncluded) {
          continue;
        }

        log(`Including row ${i} in Excel`);

        const row: string[] = [];
        for (let j = 0; j < tr.length; j++) {
          if (this.options.ignoreColumns.includes(j)) continue;
          row.push(tr[j]?.textContent?.trim() ?? '');
        }

        result.push(row);
      }
    }

    log('Final Excel rows:', result.length);
    return result;
  }

  async download(): Promise<void> {
    if (!this.table) return;

    // Build sheet data: headers + rows
    const wsData = [this.tableTitles, ...this.dataRows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Ancho automático por el contenido — midiendo la línea MÁS LARGA, no la
    // celda entera: una celda de dos carreteras tiene los dos renglones dentro,
    // y contarlos seguidos sacaba esas columnas al doble de anchas.
    const lineaMasLarga = (texto: string): number =>
      Math.max(...texto.split('\n').map((linea) => linea.length), 0);
    const colWidths = this.tableTitles.map((header, i) => {
      const maxLen = Math.max(
        header.length,
        ...this.dataRows.map((r) => lineaMasLarga(r[i] || ''))
      );
      return { wch: Math.min(maxLen + 2, 50) };
    });
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Dades');
    // `XLSX.writeFile` se guisa la descarga por dentro, y esa muere en el WebView
    // (no interpreta las URL `blob:`). Generamos el Blob y lo entregamos por el
    // punto único, que sí sabe guardar dentro de la app.
    // «Ajustar texto» en las columnas que traen varias líneas —las de un
    // recorrido multi-carretera—, y solo en esas: sin él Excel pinta el salto
    // de línea en una sola línea. Ver `xlsxWrapText`.
    await saveBlob(
      await toXlsxBlob(wb, columnsWithLineBreaks(this.dataRows)),
      this.options.filename
    );
  }
}
